export const BRIGHTNESS_THRESHOLD = 50;
export const LAPLACE_VARIANCE_THRESHOLD = 100;

export interface QualityResult {
  ok: boolean;
  tooDark: boolean;
  tooBlurry: boolean;
}

export function computeBrightness(data: Uint8ClampedArray, pixelCount: number): number {
  let total = 0;
  for (let i = 0; i < data.length; i += 4) {
    total += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  }
  return total / pixelCount;
}

export function computeLaplaceVariance(data: Uint8ClampedArray, width: number, height: number): number {
  const gray = new Float32Array(width * height);
  for (let i = 0; i < width * height; i++) {
    const idx = i * 4;
    gray[i] = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
  }

  let lapSum = 0;
  let lapSumSq = 0;
  let count = 0;

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const lap =
        gray[(y - 1) * width + x] +
        gray[(y + 1) * width + x] +
        gray[y * width + (x - 1)] +
        gray[y * width + (x + 1)] -
        4 * gray[y * width + x];
      lapSum += lap;
      lapSumSq += lap * lap;
      count++;
    }
  }

  if (count === 0) return 0;
  const mean = lapSum / count;
  return lapSumSq / count - mean * mean;
}

export function analyzeQuality(data: Uint8ClampedArray, width: number, height: number): QualityResult {
  const brightness = computeBrightness(data, width * height);
  const variance = computeLaplaceVariance(data, width, height);
  const tooDark = brightness < BRIGHTNESS_THRESHOLD;
  const tooBlurry = variance < LAPLACE_VARIANCE_THRESHOLD;
  return { ok: !tooDark && !tooBlurry, tooDark, tooBlurry };
}

export function checkImageQuality(canvas: HTMLCanvasElement): QualityResult {
  const ctx = canvas.getContext('2d');
  if (!ctx) return { ok: false, tooDark: false, tooBlurry: false };
  const { width, height } = canvas;
  const imageData = ctx.getImageData(0, 0, width, height);
  return analyzeQuality(imageData.data, width, height);
}
